import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { MapProvider } from '@/components/MapProvider';
import { DeliveryMap } from '@/components/DeliveryMap';
import { AddressInput } from '@/components/AddressInput';
import { useAddressValidation } from '@/hooks/useAddressValidation';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Loader2, Plus, Trash2, Navigation, Clock, Route, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export default function RouteOptimization({ user, onLogout }) {
  const navigate = useNavigate();
  const [stops, setStops] = useState([
    { id: 1, address: '', coordinates: null },
    { id: 2, address: '', coordinates: null }
  ]);
  const [deliveryStops, setDeliveryStops] = useState([]);
  const [polylinePath, setPolylinePath] = useState([]);
  const [optimizedRoute, setOptimizedRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const { validateAddress } = useAddressValidation();

  const handleAddressChange = (index, address, coordinates) => {
    const newStops = [...stops];
    newStops[index] = {
      ...newStops[index],
      address,
      coordinates: coordinates || newStops[index].coordinates
    };
    setStops(newStops);
  };

  const addStop = () => {
    const newId = Math.max(...stops.map(s => s.id)) + 1;
    setStops([...stops, { id: newId, address: '', coordinates: null }]);
  };

  const removeStop = (index) => {
    if (stops.length <= 2) {
      toast.error('Debes tener al menos 2 paradas');
      return;
    }
    const newStops = stops.filter((_, i) => i !== index);
    setStops(newStops);
  };

  const decodePolyline = (encoded) => {
    const points = [];
    let index = 0, lat = 0, lng = 0;

    while (index < encoded.length) {
      let result = 0, shift = 0, b;
      
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lat += dlat;

      result = 0;
      shift = 0;
      
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lng += dlng;

      points.push({
        lat: lat / 1e5,
        lng: lng / 1e5
      });
    }

    return points;
  };

  const onOptimizeRoute = async () => {
    setLoading(true);
    try {
      // Validate and geocode all addresses
      const validatedStops = [];
      
      for (let i = 0; i < stops.length; i++) {
        const stop = stops[i];
        
        if (!stop.address) {
          toast.error(`La parada ${i + 1} está vacía`);
          setLoading(false);
          return;
        }

        let coordinates = stop.coordinates;
        
        // If coordinates are not set (manual input), validate address
        if (!coordinates) {
          const validated = await validateAddress(stop.address);
          if (!validated) {
            toast.error(`No se pudo validar la dirección: ${stop.address}`);
            setLoading(false);
            return;
          }
          coordinates = {
            lat: validated.latitude,
            lng: validated.longitude
          };
        }

        validatedStops.push({
          id: `stop-${i}`,
          address: stop.address,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          status: 'pending'
        });
      }

      setDeliveryStops(validatedStops);

      // Request route optimization
      if (validatedStops.length > 1) {
        const response = await axios.post('/delivery/optimize-route', {
          origin: { 
            latitude: validatedStops[0].latitude,
            longitude: validatedStops[0].longitude
          },
          destinations: validatedStops.slice(1).map(s => ({
            latitude: s.latitude,
            longitude: s.longitude
          }))
        });

        setOptimizedRoute(response.data);
        
        // Decode polyline and set path
        if (response.data.polyline) {
          const decodedPath = decodePolyline(response.data.polyline);
          setPolylinePath(decodedPath);
        }

        toast.success('Ruta optimizada correctamente');
      }
    } catch (error) {
      console.error('Route optimization failed:', error);
      toast.error('Error al optimizar la ruta: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} title="Optimización de Rutas">
      <div className="p-6 bg-[#EAE0D5] min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-[#224049]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl font-bold text-[#224049]">
              <Navigation className="inline mr-2" />
              Optimización de Rutas
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel - Route Planning */}
            <Card className="lg:col-span-1 bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Route className="w-5 h-5" />
                  Planificador de Rutas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {stops.map((stop, index) => (
                    <div key={stop.id} className="relative">
                      <label className="text-sm font-medium text-gray-700 mb-1 block">
                        {index === 0 ? 'Punto de Inicio' : `Parada ${index}`}
                      </label>
                      <div className="flex gap-2">
                        <MapProvider>
                          <AddressInput
                            value={stop.address}
                            onChange={(address, coords) => handleAddressChange(index, address, coords)}
                            placeholder={index === 0 ? "Ubicación de inicio" : `Dirección de parada ${index}`}
                          />
                        </MapProvider>
                        {stops.length > 2 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeStop(index)}
                            className="flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={addStop}
                  className="w-full mt-3"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Parada
                </Button>

                <Button
                  onClick={onOptimizeRoute}
                  disabled={loading || stops.length < 2}
                  className="w-full mt-3 bg-[#224049] hover:bg-[#1a3138]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Optimizando...
                    </>
                  ) : (
                    <>
                      <Navigation className="w-4 h-4 mr-2" />
                      Optimizar Ruta
                    </>
                  )}
                </Button>

                {optimizedRoute && (
                  <Card className="mt-4 bg-[#F5F1ED]">
                    <CardContent className="pt-4">
                      <h3 className="font-semibold mb-3 text-[#224049]">Resumen de Ruta</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <Route className="w-4 h-4 text-[#C38154]" />
                          <span className="font-medium">Distancia:</span>
                          <span>{(optimizedRoute.distance / 1000).toFixed(2)} km</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#C38154]" />
                          <span className="font-medium">Duración:</span>
                          <span>{Math.round(optimizedRoute.duration / 60)} minutos</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Navigation className="w-4 h-4 text-[#C38154]" />
                          <span className="font-medium">Paradas:</span>
                          <span>{deliveryStops.length}</span>
                        </div>
                      </div>

                      {optimizedRoute.legs && optimizedRoute.legs.length > 0 && (
                        <div className="mt-4">
                          <h4 className="font-medium text-sm mb-2">Tramos de la Ruta:</h4>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {optimizedRoute.legs.map((leg, idx) => (
                              <div key={idx} className="text-xs bg-white p-2 rounded">
                                <p className="font-medium">Tramo {idx + 1}</p>
                                <p className="text-gray-600">
                                  {(leg.distance / 1000).toFixed(1)} km - {Math.round(leg.duration / 60)} min
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>

            {/* Right Panel - Map */}
            <Card className="lg:col-span-2 bg-white h-[600px] lg:h-auto">
              <CardContent className="p-0 h-full">
                <MapProvider>
                  <DeliveryMap
                    stops={deliveryStops}
                    polylinePath={polylinePath}
                    onMarkerClick={(stopId) => console.log('Marker clicked:', stopId)}
                  />
                </MapProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}